import { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getOrderPaymentDetails,
  GetOrderPaymentDetailsState,
  resetGetOrderPaymentDetails,
  selectGetOrderPaymentDetails,
} from "../slices/get-order-payment-details.slice";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { PaymentProcessPay } from "../components/payment-process-pay";
import { PaymentProcessConfirm } from "../components/payment-process-confirm";
import { PaymentProcessComplete } from "../components/payment-process-complete";
import { PaymentOrderInformation } from "../components/payment-order-information";
import { PaymentProductInfo } from "../components/payment-product-info";
import { PAYMENT_PROCESS_TABS } from "features/stock-ordering/core/domain/payment.model";
import { Box, Step, StepLabel, Stepper } from "@mui/material";
import { FaHistory } from "react-icons/fa";

interface PaymentProcessModalProps {
  open: boolean;
  onClose: () => void;
  currentTab: number;
}

interface PaymentHistoryItem {
  title: string;
  description: string;
  datetime: string;
  first_name: string;
  last_name: string;
}

export function PaymentProcessModal(props: PaymentProcessModalProps) {
  const dispatch = useAppDispatch();
  const { orderId } = useParams();

  const getOrderPaymentDetailsState = useAppSelector(
    selectGetOrderPaymentDetails,
  );
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const mainDivRef = useRef<HTMLDivElement>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { order, payment, supplier_payment_info, payment_history } =
    getOrderPaymentDetailsState.data ?? {};

  // Determine if user is a seller/supplier (selling view role)
  const isSeller =
    (getAdminSessionState.data?.admin.user_details.sos_groups_seller.length ??
      0) > 0;

  // Fetch order payment details when modal opens
  useEffect(() => {
    if (orderId && props.open && !getOrderPaymentDetailsState.data) {
      dispatch(getOrderPaymentDetails(Number(orderId)));
    }
  }, [dispatch, props.open, orderId, getOrderPaymentDetailsState.data]);

  const handleCloseModal = () => {
    dispatch(resetGetOrderPaymentDetails());
    setShowHistory(false);
    document.body.classList.remove("overflow-hidden");
    props.onClose();
  };

  if (props.open) {
    mainDivRef.current?.focus();
    document.body.classList.add("overflow-hidden");
  } else {
    document.body.classList.remove("overflow-hidden");
    return null;
  }

  const getModalTitle = () => {
    const tab = PAYMENT_PROCESS_TABS[props.currentTab];
    return tab?.name.toUpperCase() ?? "PAYMENT";
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-PH", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
  };

  const renderProcessAction = () => {
    switch (props.currentTab) {
      case 0: // Pay stage - only store/order users can upload payment
        if (isSeller) {
          // Sellers can view but not act on Pay stage
          return (
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 text-center">
              <p className="text-yellow-800 text-sm">
                This order is awaiting payment from the store. You will be able
                to confirm the payment once it has been submitted.
              </p>
            </div>
          );
        }
        return (
          <PaymentProcessPay
            orderId={orderId}
            supplierPaymentInfo={supplier_payment_info}
            onClose={handleCloseModal}
          />
        );
      case 1: // Confirm Payment stage - only sellers/suppliers can confirm
        if (!isSeller) {
          // Store users can view the submitted proof but not confirm/reject
          return (
            <PaymentProcessConfirm
              orderId={orderId}
              paymentDetails={payment}
              onClose={handleCloseModal}
              readOnly={true}
            />
          );
        }
        return (
          <PaymentProcessConfirm
            orderId={orderId}
            paymentDetails={payment}
            onClose={handleCloseModal}
            readOnly={false}
          />
        );
      case 2: // Complete Payment stage - viewable by all
        return <PaymentProcessComplete paymentDetails={payment} />;
      default:
        return (
          <div className="text-gray-500 text-sm">
            No process available for this step.
          </div>
        );
    }
  };

  const historyItems: PaymentHistoryItem[] = payment_history ?? [];

  return (
    <div
      ref={mainDivRef}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleCloseModal();
        }
      }}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-auto bg-black bg-opacity-30 backdrop-blur-sm"
    >
      <div className="w-[97%] lg:w-[900px] my-5 rounded-[10px]">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-[10px] flex items-center justify-between p-4">
          <span className="text-2xl text-white uppercase font-bold">
            {getModalTitle()}
          </span>

          <div className="space-x-3">
            <button
              className="text-2xl text-white hover:text-gray-200"
              onClick={handleCloseModal}
            >
              <IoMdClose />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 bg-paper border-b-2 border-l-2 border-r-2 border-green-600 space-y-5 select-none rounded-b-[10px]">
          {getOrderPaymentDetailsState.status ===
          GetOrderPaymentDetailsState.inProgress ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : getOrderPaymentDetailsState.data ? (
            <>
              {/* Order Information */}
              <PaymentOrderInformation orderData={order} />

              {/* Product Information */}
              <PaymentProductInfo orderId={orderId} />

              {/* Process Actions */}
              <div className="border rounded-2xl shadow-sm p-5 bg-white">
                <div className="mt-3">{renderProcessAction()}</div>
              </div>

              {/* Payment History */}
              {historyItems.length > 0 && (
                <div className="border rounded-2xl shadow-sm bg-white overflow-hidden">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <FaHistory className="text-green-600" />
                      <span className="text-base font-semibold md:text-lg md:font-bold">
                        Payment History
                      </span>
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-700">
                        {historyItems.length}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {showHistory ? "Hide" : "Show"}
                    </span>
                  </button>

                  {showHistory && (
                    <div className="px-5 pb-5">
                      <Box sx={{ maxWidth: "100%" }}>
                        <Stepper
                          activeStep={historyItems.length}
                          orientation="vertical"
                        >
                          {historyItems.map(
                            (step: PaymentHistoryItem, index: number) => (
                              <Step key={index}>
                                <StepLabel sx={{ marginRight: 5 }}>
                                  <div className="flex flex-col">
                                    <span className="text-sm md:text-base font-semibold">
                                      {step.title}
                                    </span>
                                    {step.description && (
                                      <span className="text-xs text-gray-500">
                                        {step.description}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-400">
                                      {formatDate(step.datetime)}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      Performed by:{" "}
                                      {step.first_name + " " + step.last_name}
                                    </span>
                                  </div>
                                </StepLabel>
                              </Step>
                            ),
                          )}
                        </Stepper>
                      </Box>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Failed to load order details. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
